import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Image as ImageIcon, Pin } from 'lucide-react';
import { getFullImageUrl } from '../../utils/imageUtils';
import { getDeadlineFlag, DEADLINE_STYLES } from '../../utils/deadlineFlag';

const OrderItem = ({ order, isSelected, onClick, isPinned, onTogglePin, customFilters, onAddToFilter, onRemoveFromFilter }) => {
  // Get first uploaded image as thumbnail
  const thumbnailPath = order.uploaded_files?.find(file =>
    file?.toLowerCase().endsWith('.jpg') ||
    file?.toLowerCase().endsWith('.jpeg') ||
    file?.toLowerCase().endsWith('.png') ||
    file?.toLowerCase().endsWith('.gif')
  );

  const thumbnail = thumbnailPath ? getFullImageUrl(thumbnailPath) : null;

  const handlePinClick = (e) => {
    e.stopPropagation();
    if (onTogglePin) onTogglePin(order.id);
  };

  // Get which filters this order belongs to
  const orderFilters = (customFilters || []).filter(f => f.order_ids?.includes(order.id));

  return (
    <div
      onClick={onClick}
      className={`px-3 py-3 cursor-pointer border-b border-gray-200 hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-gray-100' : isPinned ? 'bg-emerald-50/40' : 'bg-white'
      }`}
      data-testid={`order-item-${order.order_number}`}
    >
      <div className="flex space-x-3">
        {/* Photo Thumbnail */}
        <div className="flex-shrink-0">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={order.order_number || 'Order'}
              className="w-16 h-16 object-cover rounded-md border border-gray-200"
            />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-md flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-gray-500" />
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col mb-1">
            <h3 className="font-bold text-gray-900 text-sm flex items-center justify-between">
              <span className="truncate">{order.order_number || 'N/A'}</span>
              <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                {/* Pin icon */}
                <button
                  onClick={handlePinClick}
                  className={`p-0.5 rounded transition-colors ${
                    isPinned
                      ? 'text-emerald-600 hover:text-emerald-800'
                      : 'text-gray-300 hover:text-gray-500'
                  }`}
                  title={isPinned ? 'Unpin order' : 'Pin order'}
                  data-testid={`pin-btn-${order.order_number}`}
                >
                  <Pin className={`w-4 h-4 ${isPinned ? 'fill-current' : ''}`} />
                </button>
                {/* WhatsApp-style green unread badge */}
                {order.unreadCount > 0 && (
                  <span
                    className="bg-[#25D366] text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1"
                    data-testid={`unread-badge-${order.order_number}`}
                  >
                    {order.unreadCount}
                  </span>
                )}
              </div>
            </h3>
            {order.auto_generated_number && (
              <p className="text-xs text-gray-500 font-mono">
                System: {order.auto_generated_number}
              </p>
            )}
          </div>

          {/* Order Type Badge */}
          {order.order_type && (
            <div className="mb-1">
              <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                {order.order_type}
              </span>
            </div>
          )}

          {/* Product Name */}
          {order.title && (
            <p className="text-xs text-gray-800 font-semibold mb-1">
              📦 {order.title}
            </p>
          )}

          {/* Deadline Flag */}
          {(() => {
            const flag = getDeadlineFlag(order);
            if (flag.level === 'gray' && !order.delivery_date) return null;
            return (
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {flag.level !== 'gray' && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${DEADLINE_STYLES[flag.level]}`}>
                    {flag.label}
                  </span>
                )}
                {flag.level === 'gray' && order.delivery_date && (
                  <span className="text-xs text-orange-600 font-medium">
                    📅 {new Date(order.delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })}
                  </span>
                )}
                {order.rework_count > 0 && (
                  <span className="text-[10px] font-bold text-amber-600">↻ {order.rework_count}</span>
                )}
              </div>
            );
          })()}

          {/* Order Owner */}
          {order.order_owner_name && (
            <p className="text-xs text-emerald-600 font-medium mb-1">
              👤 {order.order_owner_name}
            </p>
          )}

          {/* Filter tags */}
          {orderFilters.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {orderFilters.map(f => (
                <span key={f.name} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                  {f.name}
                </span>
              ))}
            </div>
          )}

          {/* Last Message Time */}
          {order.last_message_time && (
            <p className="text-xs text-gray-500">
              {(() => {
                try {
                  const date = new Date(order.last_message_time + 'Z');
                  return isNaN(date.getTime()) ? '' : formatDistanceToNow(date, { addSuffix: true });
                } catch {
                  return '';
                }
              })()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderItem;
